import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Account } from './Account.js';
import { Machine } from './Machine.js';

@Entity()
export class Case extends MongoEntity {
  static override _class = 'Case';
  static override _collectionName = 'cases';

  constructor(data: PartialEntityData<Case>) {
    super(data);
  }

  // System fields
  @Field(() => String) caseID: Maybe<string>;
  @Field(() => String) rowChecksum: Maybe<string>;
  @Field(() => Date) modifiedOn: Maybe<Date>;

  // Case details
  @Field(() => String) caseNumber: Maybe<string>;
  @Field(() => String) caseTitle: Maybe<string>;
  @Field(() => Date) createdOn: Maybe<Date>;
  @Field(() => String) createdBy: Maybe<string>;
  @Field(() => String) caseType: Maybe<string>;
  @Field(() => String) status: Maybe<string>;
  @Field(() => String) priority: Maybe<string>;

  // Account fields (flattened)
  @Field(() => String) accountName: Maybe<string>;
  @Field(() => String) accountNumber: Maybe<string>;
  @Field(() => String) address1: Maybe<string>;
  @Field(() => String) address1AddressType: Maybe<string>;
  @Field(() => String) address1City: Maybe<string>;
  @Field(() => String) address1Country: Maybe<string>;
  @Field(() => String) address1Street1: Maybe<string>;
  @Field(() => String) address1ZipPostalCode: Maybe<string>;
  @Field(() => String) email: Maybe<string>;
  @Field(() => String) growthAccount: Maybe<string>;

  // Links
  @Field(() => String) account: Maybe<string>;
  @Field(() => String) contact: Maybe<string>;

  // Other fields
  @Field(() => String) phoneNumber: Maybe<string>;
  @Field(() => String) description: Maybe<string>;
  @Field(() => String) escalated: Maybe<'Yes' | 'No'>;
  @Field(() => Date) escalatedOn: Maybe<Date>;
  @Field(() => String) firstContactBy: Maybe<string>;
  @Field(() => String) firstContacted: Maybe<string>;
  @Field(() => Date) firstContactedOn: Maybe<Date>;
  @Field(() => String) growth: Maybe<string>;
  @Field(() => String) initiatedBy: Maybe<string>;

  // dossier number
  @Field(() => String) machine: Maybe<string>;

  @Field(() => String) machineName: Maybe<string>;
  @Field(() => String) modifiedBy: Maybe<string>;
  @Field(() => Date) modifiedOn2: Maybe<Date>;

  static override async transformBeforeUpdate(oldEntity: PartialEntityData<Case>, update: Case): Promise<void> {
    update.modifiedOn = new Date();

    return super.transformBeforeUpdate(oldEntity, update);
  }

  // knowledge queue
  // this will later be its own api call to the zwickroell ticketing system, to move the ticket into this queue
  @Field(() => String, {
    resolve: function (this: Case) {
      return async () => {
        const machine = await loadMongoData(Machine, { dossierNumber: this.machine });
        if (!machine[0]) return null;

        const productGroup = machine[0].productGroup;
        if (!productGroup) return null;

        const hpg: string[] = [
          'Analyser ASMEC',
          'Brinell IND',
          'DuraPro',
          'Hardness',
          'Hardness non-Zwick',
          'Hardness US',
          'Hardness UZ',
          'Jom iny IND',
          'Modernization Hardness UZ',
          'Nanoindenter ASMEC',
          'Portable IND',
          'Rockwell Comp.',
          'Rockwell IND',
          'Rockwell ZET',
          'Software ASMEC',
          'Special Machine',
          'Universal IND',
          'Universal ZET',
          'Vickers',
          'Vickers IND',
          'Vickers ZET',
          'VRSC',
        ];
        const dyn: string[] = [
          'High Speed Testing Machine UP',
          'High Speed Testing Machine UZ',
          'LTM UP',
          'Modernization Dynamic UZ',
          'UTM Dynamic',
          'UTM dynamic non-Zwick',
          'UTM dynamic UP',
          'UTM dynamic UP ZRT',
          'UTM dynamic UZ',
          'Vibrophore',
          'Vibrophore non-Zwick',
          'Vibrophore UP',
          'Vibrophore UZ',
        ];
        const aut: string[] = ['Automation UP', 'Autom UZ'];

        if (hpg.includes(productGroup)) return 'HPG';
        if (dyn.includes(productGroup)) return 'DYN';
        if (aut.includes(productGroup)) return 'AUT';

        return 'STA';
      };
    },
  })
  knowledgeQueue: Maybe<string>;

  @Field(type => Machine, {
    // @ts-expect-error the type is correct
    resolve: function (this: Case) {
      return async (gqlFields: BaseQLSelectionSet<Machine>) => {
        const machines = await loadMongoData(Machine, { dossierNumber: this.machine });
        return machines[0];
      };
    },
  })
  machineResolved: Maybe<Machine>;

  @Field(type => Account, {
    // @ts-expect-error the type is correct
    resolve: function (this: Case) {
      return async (gqlFields: BaseQLSelectionSet<Account>) => {
        const machines = await loadMongoData(Machine, { dossierNumber: this.machine });
        const machine = machines[0];
        if (!machine) return null;

        const accounts = await loadMongoData(Account, { accountNumber: machine.commercialAccountNumber });
        return accounts[0];
      };
    },
  })
  accountResolved: Maybe<Account>;

  @Field(type => [String], {
    resolve: function (this: Case) {
      return async () => {
        const ticket = await (
          await getRawCollection<EntityData<Case>>(Case._collectionName)
        ).findOne({ _id: this._id }, { projection: { vapiCallLog: 1 } });
        if (!ticket) return [];

        return typeof ticket.vapiCallLog !== 'string'
          ? Array.isArray(ticket.vapiCallLog)
            ? ticket.vapiCallLog.map((item: any) => JSON.stringify(item))
            : [JSON.stringify(ticket.vapiCallLog)]
          : ticket.vapiCallLog;
      };
    },
  })
  vapiCallLog: Maybe<string[]>;
}

export const getCaseForCaseNumberFuzzy = async (caseNumber: Maybe<string>): Promise<EntityData<Case> | null> => {
  if (!caseNumber) {
    return null;
  }

  const collection = await getCollection<Case>(Case._class);

  // Take only digits and try to find that caseNumber
  const digits = caseNumber.replace(/[^0-9]/g, '');
  const targetCase = `CAS${Array.from(digits)
    .map(d => `(\\D*${d})`)
    .join('')}`;

  logger.debug({ targetCase, digits, caseNumber }, 'searching for ticket with pattern');

  const ticket = await collection.findOne({ caseNumber: { $regex: targetCase, $options: 'i' } });
  return ticket;
};
